<template>
    <div class="row">
      <div class="col-12">
        <p class="lead">AR</p>

        <confined-space-filter @filter="onFilter"></confined-space-filter>

        <router-link
          :to="{
            name: 'confined-spaces-risk-analyses-multiple',
            params: { confinedSpacesIds: confined_spaces.map(cs => cs.id), risks: getRiskCategories() }
          }"
          class="no-print btn btn-primary mb-2"
        >
          <font-awesome-icon :icon="['fas', 'print']"></font-awesome-icon>
          Salvar / Imprimir ARs
        </router-link>

        <DataTable
          :value="confined_spaces"
          :lazy="true"
          :paginator="true"
          :paginator-position="'bottom'"
          :rows="25"
          :rows-per-page-options="[25, 50, 75, 100, 200]"
          :total-records="totalRecords"
          :loading="loading"
          :row-hover="true"
          @page="onPage($event)"
          @sort="onSort($event)"
          ref="dt"
          data-key="id"
          class="p-datatable-gridlines p-datatable-sm table-striped"
        >
          <template #empty> Nenhum item encontrado </template>
          <template #loading> Carregando </template>
          <ColumnGroup type="header">
            <Row>
              <Column class="text-center bg-primary text-light" header="Foto"></Column>
              <Column class="text-center bg-primary text-light" header="Identificação"></Column>
              <Column class="text-center bg-primary text-light" header="Localização"></Column>
              <Column class="text-center bg-primary text-light" header="Perigos"></Column>
              <Column class="text-center bg-primary text-light" header="Galeria"></Column>
              <Column class="text-center bg-primary text-light" header="Situações"></Column>
              <Column class="text-center bg-primary text-light" header="Risco&nbsp;Geral"></Column>
              <Column class="text-center bg-primary text-light no-print" header="Ações" ></Column>
            </Row>
          </ColumnGroup>
          <Column class="text-center">
            <template #body="{ data }">
              <img
                class="table-image expandable-image"
                :src="data.picture_url"
                alt=""
                @click="showSingleImage(data.picture_url)"
              />
            </template>
          </Column>

          <Column class="text-center">
            <template #body="{ data }">
              <div><b>Nome E.C.:</b> {{ data.name }}</div>
              <div>
                <b>Tipo E.C.:</b> {{ data.confined_space_type.name || '-' }}
              </div>
              <div><b>Tag E.C.:</b> {{ data.confined_space_tag }}</div>
              <div><b>Tag Ambra:</b> {{ data.ambra_tag }}</div>
              <div><b>ID:</b> {{ data.id }}</div>
            </template>
          </Column>

          <Column class="text-center">
            <template #body="{ data }">
              <div><b>Área:</b> {{ data.company_area.name }}</div>
              <div><b>Setor:</b> {{ data.company_sector.name }}</div>
              <div><b>Complemento:</b> {{ data.company_complement.name }}</div>
            </template>
          </Column>

          <Column class="text-center">
            <template #body="{ data }">
              <span>COMBINAÇÃO</span>
            </template>
          </Column>

          <Column class="text-center">
            <template #body="{ data }">
              <div>
                <analysis-images-gallery
                  :images="getAnalysisImages(data)"
                  @open-gallery="handleOpenGallery"
                ></analysis-images-gallery>
              </div>
            </template>
          </Column>

          <Column class="text-center">
            <template #body="{ data }">
              <div class="d-flex flex-column align-items-center">
                <div>
                  <table class="mb-2 inner-table hrn-level-table">
                    <tbody>
                      <tr>
                        <td :style="{ fontWeight: 'bold' }">
                          {{ getLevelCountValue(data.risk_analysis_descriptions.filter(rad => rad.calculated_hrn_level == 0).length) }}
                        </td>
                        <td>Conforme</td>
                      </tr>
                      <tr>
                        <td :style="{ fontWeight: 'bold' }">
                          {{ getLevelCountValue(data.risk_analysis_descriptions.filter(rad => rad.calculated_hrn_level == 1).length) }}
                        </td>
                        <td :style="{ backgroundColor: getRiskColor(1) }">
                          Muito baixo
                        </td>
                      </tr>
                      <tr>
                        <td :style="{ fontWeight: 'bold' }">
                          {{ getLevelCountValue(data.risk_analysis_descriptions.filter(rad => rad.calculated_hrn_level == 2).length) }}
                        </td>
                        <td :style="{ backgroundColor: getRiskColor(2) }">
                          Baixo
                        </td>
                      </tr>
                      <tr>
                        <td :style="{ fontWeight: 'bold' }">
                          {{ getLevelCountValue(data.risk_analysis_descriptions.filter(rad => rad.calculated_hrn_level == 3).length) }}
                        </td>
                        <td :style="{ backgroundColor: getRiskColor(3) }">
                          Médio
                        </td>
                      </tr>
                      <tr>
                        <td :style="{ fontWeight: 'bold' }">
                          {{ getLevelCountValue(data.risk_analysis_descriptions.filter(rad => rad.calculated_hrn_level == 4).length) }}
                        </td>
                        <td :style="{ backgroundColor: getRiskColor(4) }">
                          Alto
                        </td>
                      </tr>
                      <tr>
                        <td :style="{ fontWeight: 'bold' }">
                          {{ getLevelCountValue(data.risk_analysis_descriptions.filter(rad => rad.calculated_hrn_level == 5).length) }}
                        </td>
                        <td :style="{ backgroundColor: getRiskColor(5) }">
                          Muito alto
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="total">
                  <b>Total: </b> <span>{{ data.risk_analysis_descriptions?.length || '-' }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column class="text-center">
            <template #body="{ data }">
              <div
                v-bind:class="{ 'border': data.risk_analysis_descriptions.length != 0}"
                class="general-risk"
                :style="{
                  backgroundColor: getGeneralRiskColor(data.general_risk),
                }"
              >
                {{ getGeneralRiskText(data.general_risk) }}
              </div>
            </template>
          </Column>

          <Column class="text-center no-print">
            <template #body="{ data }">
              <div>
                <router-link
                  :to="{ name: 'confined-spaces-risk-analyses', params: { confinedSpaceId: data.id, risks: getRiskCategories() }}"
                  class=""
                >
                  Ver AR
                </router-link>
              </div>
              <div>
                <router-link
                  :to="{
                    name: 'confined-spaces-action-plan-form',
                    params: { filters: JSON.stringify({ confinedSpaceId: data.id }) },
                  }"
                  class=""
                >
                  Plano de ação
                </router-link>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
</template>

<script>
  import { computed, defineAsyncComponent } from 'vue'
  import { useStore } from 'vuex'

  import DataTable from 'primevue/datatable'

  import ColumnGroup from 'primevue/columngroup'
  import Column from 'primevue/column'
  import Row from 'primevue/row'

  import Button from 'primevue/button'

  import AnalysisImagesGallery from './components/AnalysisImagesGallery'

  import useImageViewer from '@/modules/ImageViewer.js'
  import useNoncomplianceModelDegreeOfRisk from '@/modules/NoncomplianceModelDegreeOfRisk'
  import useDegreeOfRisk from '@/modules/DegreeOfRiskUtil'
  import { useRouter } from 'vue-router'

  const ConfinedSpaceFilter = defineAsyncComponent({
    loader: () => import('@/components/confined_space/Filter.vue'),
  })

  export default {
    setup() {
      const store = useStore()
      const router = useRouter()
      const selected_order_of_service = computed(() => store.getters.selected_order_of_service)
      const {
        listNoncomplianceModelDegreesOfRisk,
        noncompliance_model_degrees_of_risk
      } = useNoncomplianceModelDegreeOfRisk()
      const { getRiskColor } = useDegreeOfRisk()
      const { showImageIndex, showSingleImage } = useImageViewer()

      return {
        selected_order_of_service,
        noncompliance_model_degrees_of_risk,
        listNoncomplianceModelDegreesOfRisk,
        getRiskColor,
        showImageIndex,
        showSingleImage,
        router,
      }
    },
    components: {
      DataTable,
      ColumnGroup,
      Column,
      Row,
      ConfinedSpaceFilter,
      AnalysisImagesGallery,
      Button,
    },
    data() {
      return {
        confined_spaces: [],
        totalRecords: 0,
        lazyParams: {},
        filters: {},
        loading: true,
        registers: [],
      }
    },
    mounted() {
      this.lazyParams = {
        first: 0,
        rows: this.$refs.dt.rows,
        sortField: null,
        sortOrder: null,
        filters: this.filters,
        registros: [],
      }
      this.listNoncomplianceModelDegreesOfRisk()
      this.loadLazyData()
    },
    methods: {
      loadLazyData(page = 1) {
        const app = this
        this.loading = true

        const queryString = this.getQueryString(page, this.lazyParams.rows)

        if (this.selected_order_of_service.id) {
          app.axios
            .get(
              `/api/confined_spaces/all?order_of_service_id=${this.selected_order_of_service.id}${queryString}`,
            )
            .then(({data}) => {
              let confined_spaces = data.data
              confined_spaces = confined_spaces.map(cs => {
                if (cs.risk_analysis_descriptions.length > 0)
                  cs.general_risk = this.getRiskLevel(cs.risk_analysis_descriptions)
                else
                  cs.general_risk = 0
                return cs
              })
              app.confined_spaces = confined_spaces.sort((a, b) => 
                (a.general_risk > b.general_risk ? -1 : 1)
              )
              this.totalRecords = data.total
              this.registers = app.confined_spaces
            })
            .catch(error => {
              console.log(error)
            })
            .finally(() => {
              app.loading = false
            })
        }
      },
      getQueryString(page, perPage) {
        let query = ''

        if (page) query += `&page=${page}`

        if (perPage) query += `&perPage=${perPage}`

        if (this.filters) {
          const queryStringFilters = Object.keys(this.filters)
            .filter((key) => this.filters[key])
            .map((key) => key + '=' + this.filters[key])
            .join('&')

          query += `&${queryStringFilters}`
        }

        return query
      },
      formatHrnResult(hrn) {
        const hrnFormatted = parseFloat(hrn).toFixed(3)
        if (isNaN(hrnFormatted)) return ''
        return hrnFormatted
      },
      getLevelCountValue(levelCount) {
        return !isNaN(levelCount) ? levelCount : '-'
      },
      getRiskLevel(rads) {
        return Math.max.apply(Math, rads.map(a => a.calculated_hrn_level))
      },
      getGeneralRiskText(risk) {
        const degrees = Object.assign(this.noncompliance_model_degrees_of_risk, {})
        const text = degrees.filter(non => non.magnitude == risk)[0].name
        return text
      },
      getGeneralRiskColor(risk) {
        const degrees = Object.assign(this.noncompliance_model_degrees_of_risk, {})
        const magnitude = degrees.filter(non => non.magnitude == risk)[0].magnitude
        return this.getRiskColor(magnitude)
      },
      getAnalysisImages(data) {
        let images = data?.analysis_pictures?.split(',')
        images = images.filter(image => image != "null")

        if (!images || images[0] == "") return []
        this.imagens = images;

        return images
      },
      onPage(event) {
        this.lazyParams = event

        const { page } = event

        this.loadLazyData(page + 1)
      },
      onFilter($event) {
        this.filters = $event

        this.loadLazyData()
      },
      handleOpenGallery(event) {
        const { images, index } = event

        this.showImageIndex(images, index)
      },
      getRiskCategories() {
        if (!this.filters.show_only_selected_risk_categories) return []

        return this.filters.adequacy_status
      }
    },
    watch: {
      selected_order_of_service() {
        this.loadLazyData()
      },
    },
  }
</script>

<style lang="scss" scoped>
  .table-image {
    max-width: 80px;
    max-height: 100px;
    object-fit: cover;
  }

  .general-risk {
    height: 120px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
  }

  .inner-table,
  td,
  td {
    border: 1px solid #ccc;
    border-collapse: collapse;
  }

  .inner-table thead {
    background-color: #eaeaea;
  }

  .hrn-level-table {
    min-width: 120px;
    width: auto !important;

    td {
      padding: 5px;
    }
  }

  .expandable-image {
    cursor: pointer;
  }
</style>
